import React from "react";
import { IconButton, TableRow, TableCell, makeStyles } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { ThemeType } from "@theme";
import { TimereportRowSourceMark } from "@components/TimereportRowSource/TimereportRowSourceMark";

/**
 * One reported range, and where it came from (ADR-0008). The provenance is
 * optional because rows written before it was recorded still exist — the mark
 * renders "not recorded" for those rather than nothing.
 */
type Period = {
  from: string;
  to: string;
  source?: string | null;
  sourceId?: string | null;
  sourceUrl?: string | null;
};

type Props = {
  title: string;
  periods: Period[];
  onEdit: () => void;
  onDelete: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  highlightTitle?: boolean;
};

export function BasicTimeRow(props: Props) {
  const classes = useStyles({});

  return (
    <TableRow className={classes.rowWrap}>
      <TableCell>
        <strong className={props.highlightTitle ? classes.redTitle : ""}>
          {props.title}
        </strong>
      </TableCell>

      {/*
        The mark sits on the "from" cell, on the same line as the range it
        belongs to. One type collapses into a single row with a line per range,
        and those lines can legitimately come from different channels — a
        consultant reporting Monday in the app while the rest arrives from
        Bemlo — so a per-type mark would state something untrue about half of
        them.
      */}
      <TableCell>
        {props.periods?.map((x, i) => (
          <div key={i} className={classes.timeLine}>
            <TimereportRowSourceMark
              source={x.source}
              sourceId={x.sourceId}
              sourceUrl={x.sourceUrl}
            />
            <span>{x.from}</span>
          </div>
        ))}
      </TableCell>
      <TableCell>
        {props.periods?.map((x, i) => (
          <div key={i} className={classes.timeLine}>
            {x.to}
          </div>
        ))}
      </TableCell>
      <TableCell>
        {props.canEdit !== false && (
          <IconButton onClick={() => props.onEdit()} color="default" size="small">
            <Edit />
          </IconButton>
        )}
        {props.canDelete !== false && (
          <IconButton onClick={() => props.onDelete()} color="default" size="small">
            <Delete />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  rowWrap: {
    transition: "all .3s ease",
    "& button": {
      transition: "all .3s ease",
      opacity: 0
    },
    "&:hover": {
      background: "#eee",
      "& button": {
        opacity: 1
      }
    }
  },
  redTitle: {
    color: "red"
  },
  // A fixed line height on both columns, so the "to" values stay level with the
  // "from" values the marks have made taller.
  timeLine: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minHeight: 22
  }
}));
